import { Box, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';
import { Report, useGetScalingSize } from '@ui/Report';
import { SBEF_OFFSET_BOX_RATIO } from './report-product-groups.constants';
import {
  estimateOffsetBoxScaledWidth,
  formatProductGroupsChartValue,
} from './report-product-groups.helpers';

const { Text } = Report;

const OFFSET_BOX_INCREASE_BG = '#fbe7ea';
const OFFSET_BOX_INCREASE_COLOR = '#803943';
const OFFSET_BOX_DECREASE_BG = '#c8ffd6';
const OFFSET_BOX_DECREASE_COLOR = '#266b38';

export type OffsetBoxProps = {
  /** Signed delta vs baseline — negative means below baseline (reduction). */
  value: number;
  /** Context for the tooltip, e.g. SBEF group name or "total emissions". */
  label: string;
  /** Font size scaling number — box padding/icon scale proportionally. */
  fontSize: number;
  /**
   * When set, total box width scales linearly with |value| relative to this
   * reference (|value| / widthScaleReference). Icon + value stay centered.
   */
  widthScaleReference?: number;
  /** When true, removes background color and border — useful when placed inside a colored container. */
  transparent?: boolean;
  /** When true, hides the trend icon — useful in narrow containers where only the number fits. */
  hideIcon?: boolean;
};

/** Arrow + value badge for CO₂e offset vs baseline (Figma "Offset box", node 5070:14290). */
export const OffsetBox: React.FC<OffsetBoxProps> = ({
  value,
  label,
  fontSize,
  widthScaleReference,
  transparent = false,
  hideIcon = false,
}) => {
  const getScalingSize = useGetScalingSize();
  const absValue = Math.abs(value);
  const scaledBoxWidth =
    widthScaleReference != null && widthScaleReference > 0
      ? estimateOffsetBoxScaledWidth(absValue, widthScaleReference, fontSize)
      : undefined;
  const { classes } = useOffsetBoxStyles({
    getScalingSize,
    fontSize,
    scaledBoxWidth,
    transparent,
  });
  const isIncrease = value > 0;
  const color = isIncrease
    ? OFFSET_BOX_INCREASE_COLOR
    : OFFSET_BOX_DECREASE_COLOR;

  return (
    <Tooltip
      title={`${formatProductGroupsChartValue(Math.abs(value))} kgCO2e per m² ${isIncrease ? 'above' : 'below'} baseline for ${label}`}
      placement="top"
      disableInteractive
    >
      <Box
        className={classes.box}
        sx={{
          bgcolor: transparent
            ? 'transparent'
            : isIncrease
              ? OFFSET_BOX_INCREASE_BG
              : OFFSET_BOX_DECREASE_BG,
        }}
      >
        <Box
          className={classes.icon}
          sx={{ color, display: hideIcon ? 'none' : 'flex' }}
        >
          <Icon
            category="ui"
            icon={isIncrease ? 'arrowTrendUp' : 'arrowTrendDown'}
            size={getScalingSize(fontSize * SBEF_OFFSET_BOX_RATIO.iconHeight)}
          />
        </Box>
        <Text
          as="span"
          fontSize={fontSize}
          fontWeight={700}
          sx={{
            color,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
          }}
        >
          {formatProductGroupsChartValue(absValue)}
        </Text>
      </Box>
    </Tooltip>
  );
};

const useOffsetBoxStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
  fontSize: number;
  scaledBoxWidth?: number;
  transparent: boolean;
}>()((_theme, { getScalingSize, fontSize, scaledBoxWidth, transparent }) => ({
  box: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width:
      scaledBoxWidth != null ? getScalingSize(scaledBoxWidth) : 'fit-content',
    boxSizing: 'border-box',
    paddingRight: getScalingSize(fontSize * SBEF_OFFSET_BOX_RATIO.paddingRight),
    borderRadius: getScalingSize(fontSize * SBEF_OFFSET_BOX_RATIO.borderRadius),
    border: transparent
      ? 'none'
      : `${getScalingSize(fontSize * SBEF_OFFSET_BOX_RATIO.borderWidth)} solid rgba(0,0,0,0.1)`,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  icon: {
    display: 'flex',
    flexShrink: 0,
  },
}));
